import { API_METHODS } from '@compass/client/constants/api-methods';
import { InvitationStatus } from '@compass/client/types/organization';
import type { OrganizationUser } from '@compass/client/types/organization_user';
import {
  Award,
  Loader2,
  MoreVertical,
  Plus,
  Search,
  Trash2,
  User as UserIcon,
  UserCog,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PermissionGuard } from '@/components/PermissionGuard';
import { ChangeRolesDialog } from '@/components/settings/ChangeRolesDialog';
import { InviteUserDialog } from '@/components/settings/InviteUserDialog';
import { ManageSkillsDialog } from '@/components/settings/ManageSkillsDialog';
import { MemberSkillsCell } from '@/components/settings/MemberSkillsCell';
import { OrganizationTeams } from '@/components/settings/OrganizationTeams';
import { PendingInvitations } from '@/components/settings/PendingInvitations';
import { RemoveUserDialog } from '@/components/settings/RemoveUserDialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserAvatar } from '@/components/UserAvatar';
import { useOrganizationInvitations } from '@/hooks/useOrganizationInvitations';
import { useOrganizationUsers } from '@/hooks/useOrganizationUsers';
import { useCanCallAPI } from '@/hooks/usePermissions';
import { useStore } from '@/store';

const ITEMS_PER_BATCH = 20;

export const OrganizationMembers: React.FC = () => {
  const { selectedOrganizationId } = useStore();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [removeDialogUser, setRemoveDialogUser] = useState<OrganizationUser | null>(null);
  const [changeRolesUser, setChangeRolesUser] = useState<OrganizationUser | null>(null);
  const [manageSkillsUser, setManageSkillsUser] = useState<OrganizationUser | null>(null);
  const canViewSkills = useCanCallAPI(API_METHODS.UserService.GetUserSkills);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_BATCH);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const {
    data: usersData,
    isLoading: isLoadingUsers,
    isFetchingMore,
    error: usersError,
  } = useOrganizationUsers(selectedOrganizationId ?? undefined);

  const { data: invitationsData, isLoading: isLoadingInvitations } = useOrganizationInvitations(
    selectedOrganizationId ?? undefined,
    {
      limit: 100,
      statuses: [
        InvitationStatus.INVITATION_STATUS_PENDING,
        InvitationStatus.INVITATION_STATUS_EXPIRED,
      ],
    },
  );

  const allUsers = useMemo(() => usersData?.users || [], [usersData?.users]);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) {
      return allUsers;
    }
    const query = searchQuery.toLowerCase().trim();
    return allUsers.filter(
      user => user.email?.toLowerCase().includes(query) || user.name?.toLowerCase().includes(query),
    );
  }, [allUsers, searchQuery]);

  // Reset visible count when search query changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setVisibleCount(ITEMS_PER_BATCH);
  };

  const visibleUsers = useMemo(
    () => filteredUsers.slice(0, visibleCount),
    [filteredUsers, visibleCount],
  );

  const hasMore = visibleCount < filteredUsers.length;

  // Infinite scroll via IntersectionObserver
  const loadMore = useCallback(() => {
    setVisibleCount(prev => Math.min(prev + ITEMS_PER_BATCH, filteredUsers.length));
  }, [filteredUsers.length]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      },
      { rootMargin: '100px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  if (!selectedOrganizationId) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <div className="py-8 text-center text-muted-foreground">
          Please select an organization to view members.
        </div>
      </div>
    );
  }

  const invitationsCount = invitationsData?.invitations?.length || 0;
  const hasError = !!usersError;
  const isEmpty = !isLoadingUsers && filteredUsers.length === 0;
  const isSearchEmpty = isEmpty && searchQuery.trim() && allUsers.length > 0;

  return (
    <>
      <div className="rounded-lg border bg-card p-6">
        {/* Header - always visible */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">Members</h2>
            <p className="text-sm text-muted-foreground">Manage members and their roles</p>
          </div>
          <Button onClick={() => setInviteDialogOpen(true)} size="sm">
            <Plus className="mr-2 h-4 w-4" />
            Invite User
          </Button>
        </div>

        {/* Content - conditional rendering */}
        {hasError ? (
          <div className="py-8 text-center">
            <p className="text-sm text-destructive">Failed to load organization members</p>
            <p className="mt-2 text-xs text-muted-foreground">{(usersError as Error).message}</p>
          </div>
        ) : isLoadingUsers ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-64" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Tabs defaultValue="members" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-2">
              <TabsTrigger value="members">
                Members{' '}
                {isFetchingMore ? (
                  <Loader2 className="ml-1 h-3 w-3 animate-spin" />
                ) : (
                  `(${allUsers.length})`
                )}
              </TabsTrigger>
              <TabsTrigger value="invitations">
                Invitations {!isLoadingInvitations && `(${invitationsCount})`}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="members">
              {/* Search input */}
              <div className="relative my-4">
                <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full pl-9"
                />
              </div>

              {isEmpty ? (
                <div className="py-12 text-center text-muted-foreground">
                  <UserIcon className="mx-auto h-12 w-12 text-muted-foreground/50" />
                  <p className="mt-4">
                    {isSearchEmpty
                      ? `No members found matching "${searchQuery}"`
                      : 'No members found in this organization'}
                  </p>
                </div>
              ) : (
                <div className="overflow-hidden rounded-md border">
                  <div className="max-h-[400px] overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 z-10 border-b bg-card">
                        <TableRow>
                          <TableHead className="w-[50px]" />
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Title</TableHead>
                          {canViewSkills && <TableHead>Skills</TableHead>}
                          <TableHead className="sticky right-0 z-20 w-[50px] bg-card" />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {visibleUsers.map(user => (
                          <TableRow key={user.user_id} className="group">
                            <TableCell>
                              <UserAvatar
                                name={user.name}
                                email={user.email}
                                userId={user.user_id}
                                size="md"
                                className="shrink-0"
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <span className="font-medium">{user.name || 'Unnamed User'}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <span>{user.email}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-muted-foreground">
                                {user.title || '—'}
                              </span>
                            </TableCell>
                            {canViewSkills && (
                              <TableCell>
                                <MemberSkillsCell
                                  organizationId={selectedOrganizationId}
                                  userId={user.user_id}
                                />
                              </TableCell>
                            )}
                            <TableCell className="sticky right-0 z-10 bg-card group-hover:bg-muted">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => setChangeRolesUser(user)}>
                                    <UserCog className="mr-2 h-4 w-4" />
                                    Change Roles
                                  </DropdownMenuItem>
                                  <PermissionGuard
                                    apiMethod={API_METHODS.UserService.SetUserSkills}
                                  >
                                    <DropdownMenuItem onClick={() => setManageSkillsUser(user)}>
                                      <Award className="mr-2 h-4 w-4" />
                                      Manage Skills
                                    </DropdownMenuItem>
                                  </PermissionGuard>
                                  <DropdownMenuItem
                                    className="text-destructive focus:text-destructive"
                                    onClick={() => setRemoveDialogUser(user)}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Remove User
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* Infinite scroll sentinel */}
                    <div ref={sentinelRef} className="h-1" />

                    {(hasMore || isFetchingMore) && (
                      <div className="flex items-center justify-center py-3">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="invitations">
              {selectedOrganizationId && (
                <PendingInvitations organizationId={selectedOrganizationId} />
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Organization Teams Card */}
      {selectedOrganizationId && (
        <div className="mt-6">
          <OrganizationTeams organizationId={selectedOrganizationId} />
        </div>
      )}

      {/* Dialogs */}
      {selectedOrganizationId && (
        <>
          <InviteUserDialog
            organizationId={selectedOrganizationId}
            open={inviteDialogOpen}
            onOpenChange={setInviteDialogOpen}
          />
          <RemoveUserDialog
            organizationId={selectedOrganizationId}
            user={removeDialogUser}
            open={!!removeDialogUser}
            onOpenChange={open => !open && setRemoveDialogUser(null)}
          />
          <ChangeRolesDialog
            organizationId={selectedOrganizationId}
            user={changeRolesUser}
            open={!!changeRolesUser}
            onOpenChange={open => !open && setChangeRolesUser(null)}
          />
          <ManageSkillsDialog
            organizationId={selectedOrganizationId}
            user={manageSkillsUser}
            open={!!manageSkillsUser}
            onOpenChange={open => !open && setManageSkillsUser(null)}
          />
        </>
      )}
    </>
  );
};
